import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { TAttribute } from "types/Pages/ProductDelta"
import AttributeValueList from "./AttributeValueList"
import { TFilters } from "types/hooks/useTable"
import debounce from "lodash/debounce"
import { ProductsContext } from "../ProductsContext"
import Accordion from "Components/DesignSystem/Accordion/Accordion"
import Search from "Components/DesignSystem/Search/Search"
import CustomAttributeTag from 'Components/Common/CustomAttributeTag';

type MyProps = {
    attributes: TAttribute[] | null
    handleChange: (ids: string[]) => void,
    filters: TFilters
}

/**
 * List of attributes and attribute values as pills. On the DS since § 68:
 * `Accordion` (aria-expanded disclosures — the Common trio announced nothing)
 * and `Search` in place of CustomSearchBar. The 700ms debounce is the legacy
 * timing verbatim: CustomSearchBar's 200ms stage feeding the caller's 500ms
 * stage, sequential. Lazy value-fetch still fires on first expand only.
 */
const AttributeListPill = ({ attributes, filters, handleChange }: MyProps) => {
    const [selected, setSeleted] = useState<string[]>(filters.filters ?? [])
    const selectedRef = useRef<string[]>(filters.filters ?? []);
    const prodContext = useContext<any>(ProductsContext)
    const [accordionPages, setAccordionPages] = useState(new Map());
    const [attributeQuery, setAttributeQuery] = useState<string>("")
    const [searchPending, setSearchPending] = useState<boolean>(false);

    const getPage = (attributeId: string) => {
        return accordionPages.get(attributeId) || 1;
    };

    const setPage = (attributeId: string, newPage: number) => {
        setAccordionPages((prev) => {
            const updatedMap = new Map(prev);
            updatedMap.set(attributeId, newPage);
            return updatedMap;
        });
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>, attributeValueId: string, attributeId: string, attributeName: string, attributeValueName: string) => {
        e.preventDefault()

        prodContext.selectedFilterList.current = prodContext.toggleAttributeValueInPlace(prodContext.selectedFilterList.current, attributeId, attributeName, attributeValueId, attributeValueName);

        const index = selected.indexOf(attributeValueId)
        if (index === -1) setSeleted(prev => {
            selectedRef.current = [...prev, attributeValueId]
            return [...prev, attributeValueId]
        })
        else setSeleted(prev => {
            const nextAttrs = [...prev]
            nextAttrs.splice(index, 1)
            selectedRef.current = nextAttrs
            return nextAttrs
        })
    }

    const onHeaderExpanded = async (attributeId: string) => {
        const attrValues = prodContext.attributes?.filter((e: TAttribute) => e.id === attributeId)[0]?.values ?? [];
        if (attrValues?.length === 0) {
            await getAttributeValues(attributeId);
        }
    }

    const getAttributeValues = async (attributeId: string, page: number | null = 1, query: string | null = '') => {
        return await prodContext.fetchAttributeValues(attributeId, page, query, selectedRef.current);
    }

    useEffect(() => {
        handleChange(selected)
    }, [selected])

    /* Latest-ref so the memoized debounce never goes stale — the CustomSearchBar
       this replaces froze its handler at first render (a documented bug shape). */
    const searchFnRef = useRef(prodContext.handleCategorySearchChange);
    searchFnRef.current = prodContext.handleCategorySearchChange;
    const debouncedSearch = useMemo(() => debounce((value: string) => {
        setSearchPending(false);
        searchFnRef.current(value);
    }, 700), []);
    useEffect(() => () => { debouncedSearch.cancel(); }, [debouncedSearch]);

    const handleAttributeSearch = (value: string) => {
        setAttributeQuery(value);
        /* § 288 — the debounce window: `loadingAttributes` starts with the REQUEST,
           700ms later, and nothing else here knows a search is coming. Cleared inside
           the debounced call so the two hand over with no gap. */
        setSearchPending(true);
        debouncedSearch(value);
    };

    return (
        <div>
            <div className={`tw-py-2`}>
                <Search
                    value={attributeQuery}
                    onChange={handleAttributeSearch}
                    /* § 288 — the debounce, then the attribute fetch. `attributePage === 1`
                       keeps a SCROLL page out of it: the context's one effect serves both,
                       and a search always resets the page to 1 (`handleCategorySearchChange`),
                       so page 2+ can only be an infinite-scroll load. */
                    loading={searchPending || (prodContext.loadingAttributes && prodContext.attributePage === 1)}
                    loadingLabel="Searching attributes"
                    placeholder="Search Attributes"
                    aria-label="Search attributes"
                    autoComplete="off"
                    data-testid="products-filter-attribute-search"
                />
            </div>
            {
                prodContext.loadingAttributes && attributes?.length === 0 ? <div>loading...</div> : !attributes || attributes?.length === 0 ? <div>No attribute found</div> :
                    attributes
                        ?.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
                        .map((attribute) => (
                            <Accordion
                                key={attribute.id}
                                title={
                                    /* AB#14859. `title` is a ReactNode, so the glyph sits inside the
                                       header next to the name rather than needing its own row. */
                                    attribute.is_custom === true ? (
                                        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-min-w-0">
                                            <CustomAttributeTag data-testid={`attribute-custom-tag-${attribute.id}`} />
                                            <span className="tw-truncate">{attribute.name}</span>
                                        </span>
                                    ) : attribute.name
                                }
                                data-testid={`attribute-accordion-${attribute.id}`}
                                onOpenChange={(open) => { if (open) onHeaderExpanded(attribute.id); }}
                            >
                                <AttributeValueList
                                    attribute_values={attribute.values}
                                    handleClick={(e, id, attributeValueName) => handleClick(e, id, attribute.id, attribute.name, attributeValueName)}
                                    selected={selected}
                                    loadAttributeValues={(query: string, page: number) => getAttributeValues(attribute.id, page, query)}
                                    attributeValueCount={attribute.attributeValueCount}
                                    page={getPage(attribute.id)}
                                    setPage={(newPage: number) => setPage(attribute.id, newPage)}
                                    searchTestId={`attribute-accordion-${attribute.id}-value-search`}
                                    loadingTestId={`attribute-accordion-${attribute.id}-loading-more`}
                                />
                            </Accordion>
                        ))
            }
        </div>
    )
}

export default AttributeListPill
